import "./templateIcons.scss";

interface Props {
  icon: string;
  color: string;
}

const TemplateIcon = ({ icon, color }: Props) => {
  // Function to replace svg colors in different type of styles
  function replaceSvgColors(svgString: string, newColor: string) {
    let updatedSvg = svgString;

    // 1. Extract CSS rules from <style> tags and convert to inline styles
    const cssRules = new Map<string, Record<string, string>>();

    updatedSvg = updatedSvg.replace(
      /<style[^>]*>([\s\S]*?)<\/style>/gi,
      (match, cssContent) => {
        // Parse CSS rules
        const ruleMatches = cssContent.match(/\.[a-zA-Z][\w-]*\s*\{[^}]*\}/g);
        if (ruleMatches) {
          ruleMatches.forEach((rule: string) => {
            const classMatch = rule.match(/\.([a-zA-Z][\w-]*)\s*\{([^}]*)\}/);
            if (classMatch) {
              const className = classMatch[1];
              const properties = classMatch[2];

              // Parse properties
              const props: Record<string, string> = {};
              properties.split(";").forEach((prop: string) => {
                const [key, value] = prop
                  .split(":")
                  .map((s: string) => s.trim());
                if (key && value) {
                  props[key] = value;
                }
              });

              cssRules.set(className, props);
            }
          });
        }

        // Remove the style tag entirely
        return "";
      }
    );

    // 2. Apply CSS rules as direct attributes to elements with classes
    cssRules.forEach((properties, className) => {
      const classRegex = new RegExp(
        `(<[^>]+)class="([^"]*\\b${className}\\b[^"]*)"([^>]*>)`,
        "gi"
      );
      updatedSvg = updatedSvg.replace(
        classRegex,
        (match, beforeClass, classValue, afterClass) => {
          let attributes = "";

          // Convert CSS properties to SVG attributes
          Object.entries(properties).forEach(([prop, value]) => {
            if (
              prop === "fill" ||
              prop === "stroke" ||
              prop === "fill-rule" ||
              prop === "stroke-width" ||
              prop === "opacity"
            ) {
              // Replace color values with newColor for fill and stroke (including currentColor)
              if ((prop === "fill" || prop === "stroke") && value !== "none") {
                attributes += ` ${prop}="${newColor}"`;
              } else if (prop !== "fill" && prop !== "stroke") {
                attributes += ` ${prop}="${value}"`;
              }
            }
          });

          // Remove the class from the class attribute
          const updatedClasses = classValue
            .split(/\s+/)
            .filter((cls: string) => cls !== className)
            .join(" ");
          const classAttr = updatedClasses ? ` class="${updatedClasses}"` : "";

          return `${beforeClass}${classAttr}${attributes}${afterClass}`;
        }
      );
    });

    // 3. Remove empty class attributes
    updatedSvg = updatedSvg.replace(/\s+class=""\s*/g, " ");
    updatedSvg = updatedSvg.replace(/\s+class=""\s*/g, "");

    // 4. Replace any direct fill/stroke attributes (including currentColor)
    updatedSvg = updatedSvg.replace(
      /(fill|stroke)="(?!none)([^"]+)"/gi,
      (match, attr) => `${attr}="${newColor}"`
    );

    // 5. Replace fill/stroke in any remaining inline style attributes
    updatedSvg = updatedSvg.replace(
      /style="([^"]*)"/gi,
      (match, styleContent) => {
        const updatedStyle = styleContent
          .split(";")
          .map((rule: string) => {
            const [prop, value] = rule.split(":").map((s: string) => s.trim());
            if (
              (prop === "fill" || prop === "stroke") &&
              value &&
              !["none", ""].includes(value)
            ) {
              return `${prop}:${newColor}`;
            }
            return rule;
          })
          .filter((rule: string) => rule.trim())
          .join(";");
        return `style="${updatedStyle}"`;
      }
    );

    // 6. Add fill to elements that don't have any color definitions
    const drawableElements = [
      "path",
      "circle",
      "rect",
      "ellipse",
      "polygon",
      "polyline",
      "line",
      "text",
      "tspan",
      "g",
      "use",
      "symbol",
      "marker",
    ];

    drawableElements.forEach((tag) => {
      const regex = new RegExp(`<${tag}\\b([^>]*?)(\\s*/)?>`, "gi");
      updatedSvg = updatedSvg.replace(regex, (match, attrs, selfClosing) => {
        // Check if element already has fill or stroke
        const hasColorAttr = /(fill|stroke)\s*=/.test(attrs);
        const hasColorStyle = /style\s*=\s*"[^"]*(fill|stroke)\s*:/.test(attrs);

        if (!hasColorAttr && !hasColorStyle) {
          // Clean up the attributes string and ensure proper spacing
          const cleanAttrs = attrs ? attrs.trim() : "";
          const separator = cleanAttrs ? " " : "";
          const closingSlash = selfClosing ? " /" : "";

          return `<${tag}${
            cleanAttrs ? " " + cleanAttrs : ""
          }${separator}fill="${newColor}"${closingSlash}>`;
        }
        return match;
      });
    });

    // 7. Fix malformed tags where attributes are stuck to tag names (like <gfill="...">)
    drawableElements.forEach((tag) => {
      const malformedRegex = new RegExp(
        `<${tag}([a-zA-Z-]+="[^"]*")([^>]*)>`,
        "gi"
      );
      updatedSvg = updatedSvg.replace(malformedRegex, `<${tag} $1$2>`);
    });

    // 8. Clean up multiple spaces but preserve single spaces
    updatedSvg = updatedSvg.replace(/\s{2,}/g, " ");

    // 9. Handle special cases: gradients and patterns
    updatedSvg = updatedSvg.replace(
      /<stop[^>]*stop-color="(?!none)([^"]+)"[^>]*>/gi,
      (match) => match.replace(/stop-color="[^"]+"/, `stop-color="${newColor}"`)
    );

    // 10. Clean up any remaining empty <defs> or <style> tags
    updatedSvg = updatedSvg.replace(/<defs>\s*<\/defs>/g, "");
    updatedSvg = updatedSvg.replace(/<style[^>]*>\s*<\/style>/g, "");

    return updatedSvg;
  }

  return (
    <div
      className="template-icon"
      dangerouslySetInnerHTML={{
        __html: replaceSvgColors(icon, color || "#000"),
      }}
    ></div>
  );
};

export default TemplateIcon;
